import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { Eye, EyeSlash } from 'react-bootstrap-icons';

import useAuth from 'hooks/useAuthentication.js';
import { login } from 'store/slices/authentication';
import { alert, errorNotification } from 'utils';

/**
 * LoginForm component
 *
 * Formulario de inicio de sesión que permite autenticarse en la plataforma.
 * Incluye funcionalidad de mostrar/ocultar contraseña y eliminación automática de espacios en username y password.
 *
 * @component
 * @returns {JSX.Element} Formulario de Login
 */
const LoginForm = () => {
	const [redirect, setRedirect] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [password, setPassword] = useState('');
	const [username, setUsername] = useState('');

	const isAuthenticated = useAuth();
	const dispatch = useDispatch();
	const { isLoading } = useSelector((state) => state.authentication);

	/**
	 * Maneja el envío del formulario de login.
	 * Limpia espacios de las credenciales antes de enviarlas.
	 *
	 * @param {React.FormEvent<HTMLFormElement>} event - Evento del formulario
	 */
	function handleSubmit(event) {
		event.preventDefault();

		const cleanUsername = username.trim();
		const cleanPassword = password.trim();

		dispatch(login({ username: cleanUsername, password: cleanPassword }))
			.unwrap()
			.then(() => {
				setRedirect(true);
			})
			.catch((error) => {
				errorNotification(error?.data?.message || error);
			});
	}

	/**
	 * Maneja el cambio de valor en el campo username.
	 * Elimina espacios automáticamente.
	 *
	 * @param {React.ChangeEvent<HTMLInputElement>} e - Evento de input
	 */
	const handleUsernameChange = (e) => {
		const value = e.target.value.replace(/\s+/g, '');
		setUsername(value);
	};

	/**
	 * Maneja el cambio de valor en el campo password.
	 * Elimina espacios automáticamente.
	 *
	 * @param {React.ChangeEvent<HTMLInputElement>} e - Evento de input
	 */
	const handlePasswordChange = (e) => {
		const value = e.target.value.replace(/\s+/g, '');
		setPassword(value);
	};

	/**
	 * Alterna la visibilidad del campo password entre texto plano y oculto.
	 */
	const toggleShowPassword = () => {
		setShowPassword((prev) => !prev);
	};

	/**
	 * Muestra un cuadro de diálogo para recuperación de contraseña.
	 *
	 * @param {React.MouseEvent<HTMLAnchorElement>} e - Evento del enlace
	 */
	const openDialog = (e) => {
		e.preventDefault();
		alert(
			"¿Olvidaste tu contraseña?",
			"Contacte al administrador de Instituto CertifiED para recuperar tu contraseña.",
			"info"
		);
	};

	// Si está autenticado o ya hizo login, redirige a home
	if (redirect || isAuthenticated) {
		return <Navigate to="/" />;
	}

	return (
		<Form onSubmit={handleSubmit}>
			<h4>Iniciar sesión</h4>

			<Form.Group className="mb-3">
				<Form.Label>Username</Form.Label>
				<Form.Control
					placeholder="Username"
					name="username"
					type="text"
					value={username}
					onChange={handleUsernameChange}
				/>
				<Form.Control.Feedback type="invalid" />
			</Form.Group>

			<Form.Group className="mb-3">
				<Form.Label>Contraseña</Form.Label>
				<InputGroup>
					<Form.Control
						placeholder="Contraseña"
						name="password"
						type={showPassword ? "text" : "password"}
						value={password}
						onChange={handlePasswordChange}
						style={{
							borderRight: 0,
							borderColor: '#ced4da',
							backgroundColor: '#fff'
						}}
					/>
					<Button
						variant="light"
						onClick={toggleShowPassword}
						className="input-group-text"
						style={{
							borderLeft: 0,
							borderColor: '#ced4da',
							backgroundColor: '#fff'
						}}
					>
						{showPassword ? (
							<EyeSlash color="#2c7be5" />
						) : (
							<Eye color="#2c7be5" />
						)}
					</Button>
				</InputGroup>
				<Form.Control.Feedback type="invalid" />
			</Form.Group>

			<Form.Group>
				<Button type="submit" color="primary" className="mt-3 w-100" disabled={isLoading}>
					Acceder
				</Button>
			</Form.Group>

			<Form.Group className="mb-3">
				<Form.Label>
					<a
						onClick={openDialog}
						className="ms-auto text-inverse text-decoration-none text-opacity-50"
					>
						¿Olvidó su contraseña?
					</a>
				</Form.Label>
			</Form.Group>
		</Form>
	);
};

export default LoginForm;